import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useDndPatch } from './utils/react-beautiful-dnd-patch';
import { SnackbarProvider } from 'notistack';
import { HelmetProvider } from 'react-helmet-async';
import { loginSuccess } from './store/slices/authSlice';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AndroidLogin from './pages/AndroidLogin';
import AndroidDashboard from './pages/AndroidDashboard';
import HomePage from './pages/HomePage';
import NewHome from './pages/HomePageTop';
import HomePageBody from './pages/HomePageBody';
import HomePageTripDemo from './pages/HomePageTripDemo';
import Demo from './pages/Demo';
import './App.css';

// Components
import Layout from './components/Layout';
import Register from './pages/Register';
import Calendar from './pages/Calendar';
import Profile from './pages/Profile';
import Map from './pages/Map';
import Friends from './pages/Friends';
import Subscription from './pages/Subscription';
import AndroidLayout from './android/components/AndroidLayout';
import AndroidBottomNav from './android/components/AndroidBottomNav';
import Footer from './components/layout/Footer';
import { PrivacyPolicy, TermsOfService, Contact, About } from './pages/Legal';
import Pricing from './pages/Pricing';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Create theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#FF7B6B',
      light: '#FF9A8B',
      dark: '#FF5B4B',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#FFA07A',
      light: '#FFB5A7',
      dark: '#FF8B5A',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FFF5E6',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#4A5568',
      secondary: '#718096',
    },
    divider: 'rgba(255, 122, 107, 0.1)',
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.5px',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.5px',
    },
    h3: {
      fontWeight: 700,
      letterSpacing: '-0.5px',
    },
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.5px',
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: '48px', // Larger touch targets for mobile
          textTransform: 'none',
          borderRadius: '8px',
          fontWeight: 600,
        },
        contained: {
          boxShadow: '0 4px 12px rgba(255, 122, 107, 0.2)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(255, 122, 107, 0.3)',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          minHeight: '48px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
  },
});

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useSelector((state) => state.auth);
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Public Route component
const PublicRoute = ({ children, path }) => {
  const { isAuthenticated, loading } = useSelector((state) => state.auth);
  
  // Special case for register page - always allow access
  if (path === '/register') {
    return children;
  }
  
  // For other public routes, check authentication
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

// Main App Content component
const AppContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);

  // Apply the react-beautiful-dnd patch at the application level
  useDndPatch();

  useEffect(() => {
    const isAndroidWebView = window.ReactNativeWebView !== undefined;
    
    const checkAuth = async () => {
      try {
        if (isAndroidWebView) {
          // In WebView, use window object for storage
          const token = window.token || null;
          const user = window.user ? JSON.parse(window.user) : null;
          
          if (token && user) {
            dispatch(loginSuccess({ token, user }));
          } else {
            // Redirect to Android login page
            window.location.href = '/android-login';
          }
        } else {
          // Normal browser behavior
          const token = localStorage.getItem('token');
          const user = JSON.parse(localStorage.getItem('user') || 'null');
          
          const currentPath = window.location.pathname;
          
          // Only redirect to login if authenticated
          if (token && user) {
            dispatch(loginSuccess({ token, user }));
          }
        }
      } catch (error) {
        console.error('Auth check error:', error);
        if (isAndroidWebView) {
          window.location.href = '/android-login';
        }
      }
    };

    checkAuth();
  }, [dispatch, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Routes>
          {/* Public home page at root path */}
          <Route
            path="/"
            element={<HomePage />}
          />
          
          {/* New Home page with 3D animation */}
          <Route
            path="/new-home"
            element={<NewHome />}
          />
          
          {/* Beach vacation parallax page */}
          <Route
            path="/home-body"
            element={<HomePageBody />}
          />
          
          {/* Peru trip demo page */}
          <Route
            path="/trip-demo"
            element={<HomePageTripDemo />}
          />
          
          {/* Demo page */}
          <Route
            path="/demo"
            element={<Demo />}
          />
          
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute path="/login">
                <Login />
              </PublicRoute>
            }
          />
          
          {/* Skip PublicRoute for Register to bypass any potential issues */}
          <Route
            path="/register"
            element={<Register />}
          />
          
          {/* Password reset routes */}
          <Route
            path="/forgot-password"
            element={<ForgotPassword />}
          />
          <Route
            path="/reset-password"
            element={<ResetPassword />}
          />
          
          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="calendar" element={<Calendar />} />
            <Route path="profile" element={<Profile />} />
            <Route path="map" element={<Map />} />
            <Route path="friends" element={<Friends />} />
            <Route path="subscription" element={<Subscription />} />
          </Route>

          {/* For backward compatibility - redirect / to /dashboard if authenticated */}
          {isAuthenticated && (
            <Route
              path="/"
              element={<Navigate to="/dashboard" replace />}
            />
          )}

          {/* Direct route for travel plans - redirects to dashboard nested route */}
          
          {/* Legal pages */}
          <Route
            path="/about"
            element={<About />}
          />
          <Route
            path="/privacy"
            element={<PrivacyPolicy />}
          />
          <Route
            path="/terms"
            element={<TermsOfService />}
          />
          <Route
            path="/contact"
            element={<Contact />}
          />
          <Route
            path="/pricing"
            element={<Pricing />}
          />
          <Route
            path="/subscription/success"
            element={
              <ProtectedRoute>
                <SubscriptionSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription/cancel"
            element={
              <ProtectedRoute>
                <SubscriptionCancel />
              </ProtectedRoute>
            }
          />
          
          {/* Android-specific routes */}
          {window.ReactNativeWebView !== undefined && (
            <>
              <Route path="/android-login" element={<AndroidLogin />} />
              <Route
                path="/android-dashboard"
                element={
                  <ProtectedRoute>
                    <AndroidLayout>
                      <AndroidDashboard />
                      <AndroidBottomNav />
                    </AndroidLayout>
                  </ProtectedRoute>
                }
              />
            </>
          )}
        </Routes>
      </Box>
      <Footer />
    </Box>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3}>
        <HelmetProvider>
          <Router>
            <AppContent />
          </Router>
        </HelmetProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
